import { Checkbox } from 'antd'
import { Icon } from '@iconify/react'
import classNames from 'classnames'
import { useMemo, useState } from 'react'

import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  useCreatePhysicianIntroductionMutation,
  useFetchPhysicianIntroductionsQuery,
} from 'features/Procedure/state/procedure.api'

import styles from './physician-introductions.module.scss'
import {
  buildPhysicianName,
  formatPhysicianType,
  formatProviderSince,
  getIntroductionPhysicians,
  toLocalDateTime,
} from './physician-introductions.utils'

const SKELETON_KEYS = ['s1', 's2', 's3', 's4']

type PhysicianIntroductionsProps = {
  siteId: string
  patientId: number
  isOpen: boolean
}

export const PhysicianIntroductions = ({
  siteId,
  patientId,
  isOpen,
}: PhysicianIntroductionsProps) => {
  const { setNotification } = useNotification()
  const [savingPhysicianId, setSavingPhysicianId] = useState<number | null>(null)

  const { currentData, isFetching } = useFetchPhysicianIntroductionsQuery(
    { siteId, patientId },
    { skip: !isOpen || !siteId || !patientId },
  )
  const [createIntroduction] = useCreatePhysicianIntroductionMutation()

  const physicians = useMemo(() => getIntroductionPhysicians(currentData ?? []), [currentData])

  const sortedPhysicians = useMemo(
    () => [...physicians].sort((a, b) => Number(a.introduced) - Number(b.introduced)),
    [physicians],
  )

  const remainingCount = useMemo(
    () => physicians.filter((physician) => !physician.introduced).length,
    [physicians],
  )

  const isListLoading = isFetching && physicians.length === 0

  const handleMarkIntroduced = async (physicianId: number): Promise<void> => {
    setSavingPhysicianId(physicianId)
    try {
      await createIntroduction({
        siteId,
        patientId,
        physicianId,
        introducedAt: toLocalDateTime(new Date()),
      }).unwrap()
    } catch (error: unknown) {
      const description =
        error instanceof Error ? error.message : 'Failed to record the introduction'
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description,
      })
    } finally {
      setSavingPhysicianId(null)
    }
  }

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h3 className={styles.sectionTitle}>Doctors</h3>
        {!isListLoading && physicians.length > 0 && (
          <span className={styles.count}>
            {remainingCount > 0 ? `${remainingCount} to introduce` : 'All introduced'}
          </span>
        )}
      </div>

      {isListLoading ? (
        <ul className={styles.list} aria-hidden='true'>
          {SKELETON_KEYS.map((key) => (
            <li key={key} className={classNames(styles.row, styles.rowSkeleton)}>
              <span className={styles.info}>
                <span className={classNames(styles.skeletonBar, styles.skeletonName)} />
                <span className={classNames(styles.skeletonBar, styles.skeletonType)} />
              </span>
            </li>
          ))}
        </ul>
      ) : physicians.length === 0 ? (
        <p className={styles.empty}>No doctors to introduce.</p>
      ) : (
        <ul className={styles.list}>
          {sortedPhysicians.map((physician) => {
            const physicianType = formatPhysicianType(physician)
            const isSaving = savingPhysicianId === physician.user_id

            return (
              <li
                key={physician.user_id}
                className={classNames(styles.row, { [styles.rowMet]: physician.introduced })}>
                <span className={styles.info}>
                  <span className={styles.name}>{buildPhysicianName(physician)}</span>
                  {physicianType && <span className={styles.type}>{physicianType}</span>}
                </span>

                {physician.introduced ? (
                  <span className={styles.metAt}>
                    <Icon className={styles.metIcon} icon='lucide:check' width={16} height={16} />
                    {physician.provider_since
                      ? formatProviderSince(physician.provider_since)
                      : 'Met'}
                  </span>
                ) : (
                  <Checkbox
                    className={styles.checkbox}
                    checked={false}
                    disabled={isSaving}
                    onChange={() => handleMarkIntroduced(physician.user_id)}>
                    <span className={styles.checkboxLabel}>Has met</span>
                  </Checkbox>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
